/**
 * Frontend Environment Variable Validation
 * 
 * Validates required environment variables at startup.
 * Provides clear error messages for missing configuration.
 */

const REQUIRED_VARS = [
  {
    name: 'VITE_API_URL',
    description: 'Backend API URL',
    example: 'http://localhost:3001',
  },
];

const OPTIONAL_VARS = [
  {
    name: 'VITE_GA4_MEASUREMENT_ID',
    description: 'Google Analytics 4 Measurement ID',
    example: 'G-XXXXXXXXXX',
  },
];

/**
 * Validate environment variables
 * @returns {object} Validation result with errors and warnings
 */
export function validateEnv() {
  const errors = [];
  const warnings = [];
  const env = import.meta.env;

  // Check required variables
  for (const { name, description, example } of REQUIRED_VARS) {
    if (!env[name]) {
      errors.push({
        name,
        description,
        example,
      });
    }
  }

  // Check optional variables
  for (const { name, description, example } of OPTIONAL_VARS) {
    if (!env[name]) {
      warnings.push({
        name,
        description,
        example,
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Log validation errors to console
 * Only call this in development mode
 */
export function logValidationErrors(result) {
  if (result.errors.length > 0) {
    console.error('\n❌ Missing required environment variables:\n');
    result.errors.forEach(({ name, description, example }) => {
      console.error(`  • ${name}: ${description}`);
      console.error(`    Example: ${example}`);
    });
    console.error('\nCreate a .env file with these variables.');
    console.error('See .env.example for reference.\n');
  }

  if (result.warnings.length > 0) {
    console.warn('\n⚠️  Optional environment variables not set:\n');
    result.warnings.forEach(({ name, description, example }) => {
      console.warn(`  • ${name}: ${description}`);
      console.warn(`    Example: ${example}`);
    });
    console.warn('');
  }
}

/**
 * Validate environment on app startup
 * Throws error in development if validation fails
 * Shows warning in production
 */
export function validateOnStartup(isDev = import.meta.env.DEV) {
  const result = validateEnv();

  if (isDev) {
    logValidationErrors(result);

    /* c8 ignore start */
    if (!result.valid) {
      throw new Error('Environment validation failed. Check console for details.');
    }
    /* c8 ignore stop */
  } else if (!result.valid) {
    // Production: log error but don't crash
    console.error('Environment validation failed:', result.errors);
  }

  return result;
}
